import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { getFullImageUrl } from '../../../utils/imageUtils';
import AdminPagination from '../../components/AdminPagination';
import './ContactSubmissions.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const REASON_KEYS = ['general', 'feedback', 'suggestions', 'driver', 'partner', 'support', 'other'];

const STATUS_OPTIONS = [
  { value: 'new', labelKey: 'admin.contactStatusNew' },
  { value: 'archive', labelKey: 'admin.contactStatusArchive' },
  { value: 'under_investigation', labelKey: 'admin.contactStatusUnderInvestigation' },
  { value: 'spam', labelKey: 'admin.contactStatusSpam' }
];

const ContactSubmissions = () => {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const totalPages = Math.max(1, Math.ceil(submissions.length / itemsPerPage));
  const paginatedSubmissions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return submissions.slice(start, start + itemsPerPage);
  }, [submissions, currentPage, itemsPerPage]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/contact-submissions`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const getReasonLabel = (reason) => {
    if (REASON_KEYS.includes(reason)) {
      return t(`contactForm.reasons.${reason}`);
    }
    return reason || '—';
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await axios.patch(`${API_URL}/admin/contact-submissions/${id}`, { status: newStatus });
      setSubmissions(prev => prev.map(s => s._id === id ? { ...s, status: newStatus } : s));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteContactSubmissionConfirm') || 'Delete this contact submission?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/admin/contact-submissions/${id}`);
      setSubmissions(prev => prev.filter(s => s._id !== id));
    } catch (err) {
      console.error('Failed to delete submission:', err);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="contact-submissions-page">
        <div className="contact-submissions-header">
          <h1>{t('admin.contactSubmissions')}</h1>
        </div>
        <p>{t('admin.loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className="contact-submissions-page">
      <div className="contact-submissions-header">
        <h1>{t('admin.contactSubmissions')}</h1>
      </div>
      <div className="contact-submissions-table-wrap">
        <table className="contact-submissions-table">
          <thead>
            <tr>
              <th>{t('admin.contactSubmissionsDate') || 'Date'}</th>
              <th>{t('contactForm.name')}</th>
              <th>{t('contactForm.email')}</th>
              <th>{t('contactForm.phone')}</th>
              <th>{t('contactForm.reason')}</th>
              <th>{t('contactForm.message')}</th>
              <th>{t('admin.attachment') || 'Attachment'}</th>
              <th>{t('admin.contactSubmissionStatus') || 'Status'}</th>
              <th>{t('admin.actions') || 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSubmissions.length === 0 ? (
              <tr>
                <td colSpan="9">{t('admin.noContactSubmissions') || 'No contact submissions yet.'}</td>
              </tr>
            ) : (
              paginatedSubmissions.map((row) => (
                <tr key={row._id}>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>{row.name}</td>
                  <td><a href={`mailto:${row.email}`}>{row.email}</a></td>
                  <td>{row.phone || '—'}</td>
                  <td>{getReasonLabel(row.reason)}</td>
                  <td className="message-cell">{row.message || '—'}</td>
                  <td>
                    {row.attachmentFilename ? (
                      <a href={getFullImageUrl(row.attachmentFilename)} target="_blank" rel="noopener noreferrer">
                        {t('admin.viewImage') || 'View image'}
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    <select
                      className="contact-submission-status-select"
                      value={row.status || 'new'}
                      onChange={(e) => handleStatusChange(row._id, e.target.value)}
                      disabled={updatingId === row._id}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="contact-submission-delete-btn"
                      onClick={() => handleDelete(row._id)}
                      disabled={deletingId === row._id}
                      title={t('admin.delete') || 'Delete'}
                    >
                      {deletingId === row._id ? t('admin.deleting') || 'Deleting...' : t('admin.delete') || 'Delete'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default ContactSubmissions;
